import { useMemo, useState } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import UsageActivityChart, {
  TREND_WINDOW_DAYS,
} from "@/v2-backoffice-client/usage/UsageActivityChart"
import UsageAppFilterChips from "@/v2-backoffice-client/usage/UsageAppFilterChips"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import {
  formatAppCode,
  formatTrendDetail,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  filterAreaDaysByApp,
  getAreaDayApps,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import { describeTrendSeasonNote } from "@/v2-backoffice-client/usage/usage.seasons"
import type {
  PortfolioTimeframeKey,
  PortfolioWindow,
} from "@/v2-backoffice-client/portfolio/portfolio.filters"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import PortfolioTimeframeSelect from "@/v2-backoffice-client/portfolio/PortfolioTimeframeSelect"

export type PortfolioActivityHeroProps = {
  className?: string
  /** Fleet daily rows (unfiltered) — the app chips filter them locally. */
  areaDays: UsageAreaDayRow[]
  timeWindow: PortfolioWindow
  timeframeKey: PortfolioTimeframeKey
  onTimeframeChange(key: PortfolioTimeframeKey): void
}

// The Overview tab's "Daily activity" column: the fleet's weighted actions
// per day, with the window total vs the same-length window before it, app
// filter chips, the timeframe select, and the chart itself. The ONE place a
// timeframe applies on the Portfolio — the daily rows re-window; everything
// profile-derived is fixed 28d upstream. Mirrors the churn column's anatomy
// (identical section header on the same grid-row top, so the two hairlines
// align) with the window total as a clearly labeled stat line under it.
function PortfolioActivityHero(props: PortfolioActivityHeroProps) {
  const { className, areaDays, timeWindow, timeframeKey, onTimeframeChange } =
    props

  const apps = useMemo(() => getAreaDayApps(areaDays), [areaDays])
  const [appFilter, setAppFilter] = useState<string | null>(null)
  const filteredAreaDays = useMemo(
    () => filterAreaDaysByApp(areaDays, appFilter),
    [areaDays, appFilter],
  )
  // Apps actually visible in the chart (post app-filter) — drives the color
  // legend the section header renders in place of the chart's built-in one.
  const chartApps = useMemo(
    () => getAreaDayApps(filteredAreaDays),
    [filteredAreaDays],
  )

  // The Activity KPI follows the SELECTED window: daily-row sums for the
  // window vs the same-length window before it — unlike the nightly profile
  // signals, this one CAN re-window client-side.
  const windowActivity = useMemo(() => {
    const end = timeWindow.end.getTime()
    const spanMs = timeWindow.days * ONE_DAY_IN_MS
    let current = 0
    let previous = 0
    for (const row of areaDays) {
      const time = new Date(String(row.Date).slice(0, 10)).getTime()
      if (time > end || time <= end - 2 * spanMs) continue
      const value = row.WeightedCount ?? row.EventCount
      if (time > end - spanMs) current += value
      else previous += value
    }
    return {
      current,
      previous,
      trendPct: previous > 0 ? ((current - previous) / previous) * 100 : null,
    }
  }, [areaDays, timeWindow])

  const windowShortLabel =
    timeWindow.key === "24h"
      ? "24 h"
      : timeWindow.key.startsWith("q")
        ? timeWindow.key.toUpperCase()
        : `${timeWindow.days} d`

  // Vacation-overlap note: the KPI compares window-length pairs.
  const seasonNoteWindow = useMemo(
    () => describeTrendSeasonNote(timeWindow.end, timeWindow.days),
    [timeWindow],
  )

  // The window end anchors chart padding and window comparisons — for a
  // past quarter that's the quarter's last day, not today.
  const windowEnd = timeWindow.to ?? undefined

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <PortfolioSectionHeader
        eyebrow="Activity"
        title="Daily activity"
        lead="Weighted usage actions per day across the customers in scope, with the trailing seven-day line. Pick a product and a timeframe; the total compares with the same length of time before it."
        info="Weighted usage actions per day across the scoped book — heavy actions (exports, reports, saves) count more than clicks. The dashed line is the trailing 7-day average. The total above the chart sums the selected timeframe and compares it with the same-length window before it; the timeframe only re-windows this section, the profile-derived ones stay fixed at 28 days."
      >
        <span className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {chartApps.length > 1 &&
            chartApps.map((app) => (
              <span key={app} className="flex items-center gap-1">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: getAppCodeColor(app) }}
                />
                {formatAppCode(app)}
              </span>
            ))}
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block w-4 border-t-2 border-dashed border-foreground/60"
            />
            {TREND_WINDOW_DAYS}-day trend
          </span>
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1 gap-4">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-3xl font-semibold leading-none tabular-nums">
              {formatNumber(Math.round(windowActivity.current))}
            </span>
            <span
              className="text-sm text-muted-foreground"
              title="Heavy actions (exports, reports, saves) count more than clicks."
            >
              weighted usage actions · {windowShortLabel}
            </span>
            <UsageTrendBadge
              trendPct={windowActivity.trendPct}
              detail={formatTrendDetail(
                windowActivity.current,
                windowActivity.previous,
              )}
            />
            <span className="text-xs text-muted-foreground">
              {seasonNoteWindow != null ? (
                <span className="text-amber-500/90" title={seasonNoteWindow}>
                  vs previous — overlaps vacation, reads low
                </span>
              ) : (
                "vs previous"
              )}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <UsageAppFilterChips
              apps={apps}
              value={appFilter}
              onChange={setAppFilter}
            />
            <PortfolioTimeframeSelect
              value={timeframeKey}
              onChange={onTimeframeChange}
            />
          </div>
        </div>

        {/* Fixed height: without it the chart's aspect-video intrinsic
          (~width/16*9) balloons the row and the churn column next door
          spreads to match. ~200px keeps the two columns the same height. */}
        <UsageActivityChart
          areaDays={filteredAreaDays}
          windowDays={timeWindow.days}
          windowEnd={windowEnd}
          hideHeader
          className="h-[200px]"
        />
      </UsageReportCard>
    </div>
  )
}

export default PortfolioActivityHero
